import { FileText, Upload, Globe, RefreshCw, Archive } from "lucide-react"

export default function DocsContentFlow() {
  const steps = [
    {
      icon: FileText,
      title: "Draft",
      description: "Create or import content in JUHUU Dashboard",
      color: "from-blue-500 to-blue-600",
    },
    {
      icon: Upload,
      title: "Publish",
      description: "Articles go live instantly on the JUHUU Docs website",
      color: "from-green-500 to-green-600",
    },
    {
      icon: Globe,
      title: "Translate",
      description: "Manual translations can be added; missing ones are filled automatically",
      color: "from-purple-500 to-purple-600",
    },
    {
      icon: RefreshCw,
      title: "Update",
      description: "Edits trigger immediate re-summarisation and re-indexing",
      color: "from-orange-500 to-orange-600",
    },
    {
      icon: Archive,
      title: "Retire",
      description: "Unpublish or archive obsolete articles without losing historical versions",
      color: "from-gray-500 to-gray-600",
    },
  ]

  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Content Lifecycle</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            From creation to retirement, manage your knowledge base with complete control and transparency
          </p>
        </div>

        <div className="relative">
          {/* Connection Lines */}
          <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-200 via-purple-200 to-gray-200 transform -translate-y-1/2" />

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="relative">
                <div className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow relative z-10">
                  <div
                    className={`h-16 w-16 rounded-2xl bg-gradient-to-br ${step.color} flex items-center justify-center mx-auto mb-4`}
                  >
                    <step.icon className="h-8 w-8 text-white" />
                  </div>
                  <div className="text-center">
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{step.title}</h3>
                    <p className="text-sm text-gray-600">{step.description}</p>
                  </div>
                </div>

                {/* Step Number */}
                <div className="absolute -top-3 -right-3 h-8 w-8 bg-gray-900 text-white rounded-full flex items-center justify-center text-sm font-bold z-20">
                  {index + 1}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Multilingual Delivery Section */}
        <div className="mt-24 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Multilingual Delivery</h3>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Reach global audiences with intelligent language handling and automatic translation
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl p-6 text-center">
              <div className="h-12 w-12 rounded-xl bg-blue-500 flex items-center justify-center mx-auto mb-4">
                <Globe className="h-6 w-6 text-white" />
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">14 Interface Languages</h4>
              <p className="text-sm text-gray-600">Including German, English, Spanish and Mandarin</p>
            </div>

            <div className="bg-white rounded-xl p-6 text-center">
              <div className="h-12 w-12 rounded-xl bg-purple-500 flex items-center justify-center mx-auto mb-4">
                <RefreshCw className="h-6 w-6 text-white" />
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Neural Translation</h4>
              <p className="text-sm text-gray-600">AI fills missing translations automatically</p>
            </div>

            <div className="bg-white rounded-xl p-6 text-center">
              <div className="h-12 w-12 rounded-xl bg-indigo-500 flex items-center justify-center mx-auto mb-4">
                <FileText className="h-6 w-6 text-white" />
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Smart Detection</h4>
              <p className="text-sm text-gray-600">Follows user's browser or device locale</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
